import { useContext } from 'react';

import { DynamicAssessmentContext } from '../contexts/DynamicAssessmentContext';
import type { DynamicAssessmentHost } from '../types/dynamicAssessmentHost';

/**
 * The host contract, read from `DynamicAssessmentProvider`. Throws when absent —
 * `DynamicAssessmentScreen` cannot gate or report outcomes without one, and a screen
 * that silently never advances is worse than a loud failure at mount.
 *
 * Named for consistency with its siblings `useDynamicAssessmentApiClient` and
 * `useDynamicAssessmentAnalytics` — all three read `DynamicAssessmentContext`. The app-side counterpart
 * that BUILDS this contract is `useOnboardingSurveyHost` in `@/modules/rulesEngine`;
 * the names are deliberately distinct so the two sides can't be confused. This hook
 * is module-internal and never leaves the barrel.
 */
export function useDynamicAssessmentHost(): DynamicAssessmentHost {
  const value = useContext(DynamicAssessmentContext);
  if (!value?.host) {
    throw new Error(
      'DynamicAssessmentScreen requires a `host` on DynamicAssessmentProvider. ' +
        'Mount <DynamicAssessmentProvider apiClient={...} host={...}>, or render DynamicAssessmentFlow instead.'
    );
  }
  return value.host;
}
